/*
 * The category picker in a Transactions row, like QuickBooks' (Oct 30): it shows the category, or last time's in blue
 * until it is saved; typing searches; each line says its group on the right, and a subcategory sits under its main one
 * ("Sub of Vehicle repairs"). "Add a category" is always at the top. The list floats over the page, so a table that
 * scrolls sideways does not cut it off.
 */
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BooksSetup, CategoryRow } from "@steward/shared";
import { useAddCategory } from "../../api/books";
import { Choice, FormField, InputText } from "../../components/fields";
import { Icon } from "../../components/Icon";
import { Modal, useToast } from "../../components/overlays";
import { Button } from "../../components/parts";
import { errText } from "./common";

interface Line {
  value: string;
  /** What the box shows once picked: "Vehicle repairs: Tolls" for a subcategory. */
  label: string;
  name: string;
  tag: string;
  sub: boolean;
}

const ADD = "__add__";

/** Main categories in their profit and loss order, each followed by its subcategories. */
export function categoryLines(categories: readonly CategoryRow[]): Line[] {
  const shown = categories.filter((c) => !c.hidden && c.type !== "open");
  const out: Line[] = [];
  for (const c of shown.filter((x) => !x.parentId)) {
    out.push({ value: c.id, label: c.name, name: c.name, tag: c.group, sub: false });
    for (const s of shown.filter((x) => x.parentId === c.id))
      out.push({ value: s.id, label: `${c.name}: ${s.name}`, name: s.name, tag: `Sub of ${c.name}`, sub: true });
  }
  return out;
}

export function CategoryPick({
  categories,
  value,
  guessed = false,
  onPick,
  label,
}: {
  categories: CategoryRow[];
  value: string;
  /** The value is last time's category, not saved yet: shown in blue. */
  guessed?: boolean;
  onPick: (id: string) => void;
  label: string;
}) {
  const lines = useMemo(() => categoryLines(categories), [categories]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [at, setAt] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const current = lines.find((l) => l.value === value);
  const find = q.trim().toLowerCase();
  const shown = [
    { value: ADD, label: "Add a category", name: "Add a category", tag: "", sub: false },
    ...lines.filter((l) => `${l.label} ${l.tag}`.toLowerCase().includes(find)),
  ];
  const place = () => {
    const r = input.current?.getBoundingClientRect();
    if (!r) return;
    const up = r.bottom + 340 > window.innerHeight && r.top > 340;
    setAt({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: Math.max(r.width, 320), up });
  };
  const pick = (v: string) => {
    setOpen(false);
    input.current?.blur();
    if (v === ADD) setAdding(q.trim());
    else onPick(v);
    setQ("");
  };
  return (
    <span className="bk-pick">
      <input
        ref={input}
        className={`bk-pick-input${guessed ? " guessed" : ""}${current ? "" : " empty"}`}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        value={open ? q : (current?.label ?? "")}
        placeholder={open ? (current?.label ?? "Search categories") : "Needs a category"}
        title={guessed && current ? `${current.label} was used last time. Post to keep it.` : undefined}
        onFocus={() => {
          place();
          setOpen(true);
          setActive(
            current
              ? Math.max(
                  1,
                  shown.findIndex((l) => l.value === current.value),
                )
              : 1,
          );
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(1);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") setActive((a) => Math.min(a + 1, shown.length - 1));
          else if (e.key === "ArrowUp") setActive((a) => Math.max(a - 1, 0));
          else if (e.key === "Escape") input.current?.blur();
          else if (e.key === "Enter" && shown[active]) {
            e.preventDefault();
            pick(shown[active].value);
          }
        }}
      />
      {open && at
        ? createPortal(
            <div
              className="combo-list bk-pick-list"
              role="listbox"
              aria-label={label}
              style={{
                left: at.left,
                width: at.width,
                ...(at.up ? { bottom: window.innerHeight - at.top } : { top: at.top }),
              }}
            >
              {shown.map((l, i) => (
                <button
                  key={l.value}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  className={l.value === ADD ? "bk-pick-add" : l.sub ? "bk-pick-sub" : undefined}
                  ref={(el) => {
                    if (i === active && el) el.scrollIntoView({ block: "nearest" });
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(l.value)}
                >
                  {l.value === ADD ? (
                    <>
                      <Icon name="plus" size={14} /> Add a category
                    </>
                  ) : (
                    <>
                      <span>{l.name}</span>
                      <i className="muted small">{l.tag}</i>
                    </>
                  )}
                </button>
              ))}
              {shown.length === 1 ? <div className="muted small bk-pick-none">Nothing matches that</div> : null}
            </div>,
            document.body,
          )
        : null}
      {adding !== null ? (
        <NewCategory
          categories={categories}
          name={adding}
          onClose={() => setAdding(null)}
          onAdded={(id) => {
            setAdding(null);
            onPick(id);
          }}
        />
      ) : null}
    </span>
  );
}

/** A new category from the picker; it is used for the line right away. */
function NewCategory({
  categories,
  name: typed,
  onClose,
  onAdded,
}: {
  categories: BooksSetup["categories"];
  name: string;
  onClose: () => void;
  onAdded: (id: string) => void;
}) {
  const add = useAddCategory();
  const toast = useToast();
  const [name, setName] = useState(typed);
  const [type, setType] = useState<"expense" | "income">("expense");
  const [parent, setParent] = useState("");
  const mains = categories.filter((c) => !c.parentId && !c.hidden && c.type === type);
  return (
    <Modal
      title="Add a category"
      open
      onClose={onClose}
      footer={
        <Button
          disabled={!name.trim()}
          loading={add.isPending}
          onClick={() =>
            void add.mutateAsync({ name: name.trim(), type, ...(parent ? { parentId: parent } : {}) }).then(
              (c) => {
                toast("Category added");
                onAdded(c.id);
              },
              (e: unknown) => toast(errText(e), { kind: "error" }),
            )
          }
        >
          Add and use it
        </Button>
      }
    >
      <div className="stack">
        <InputText label="Name" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        <Choice
          label="Kind"
          value={type}
          onChange={(v) => {
            setType(v);
            setParent("");
          }}
          options={[
            { value: "expense", label: "Cost" },
            { value: "income", label: "Income" },
          ]}
        />
        <FormField label="Inside" htmlFor="bk-new-cat-parent">
          <select id="bk-new-cat-parent" className="bk-cat-select" value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">Its own category</option>
            {mains.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </Modal>
  );
}
